import { fraction, fromNet, rateFor, type TaxRate, toCents, toNet, toNumber, toStored } from "../../shared/money";
import type { PriceListSettings, SupplierDetails, SupplierPriceRow, TaxQuote } from "./meta";

/*
 * Supplier prices, tax-agnostic. Shared by the server and the screens; no server or browser imports.
 *
 *  - Each price is quoted with tax or without, at a tax rate (the tax paid on it): as its supplier
 *    usually quotes, unless it says otherwise (a GST-free item, a price copied from a receipt).
 *    Prices are typed as quoted and stored before tax.
 *  - What a price really costs the business: the before-tax price when tax on purchases is claimed
 *    back (registered for GST / VAT), or the price with its tax when it's a real cost (Settings).
 *    "Cheapest" compares these, so suppliers quoting differently are compared fairly.
 *  - An item's ServiceM8 cost is that real cost in the item's own basis (with its tax added if its
 *    prices include tax), as ServiceM8 stores costs.
 */

/** How a supplier usually quotes (its details), as a quote. */
export const usualQuote = (details: Pick<SupplierDetails, "pricesIncludeTax" | "purchaseTaxRateUuid">): TaxQuote => ({
    rateUuid: details.purchaseTaxRateUuid,
    includesTax: details.pricesIncludeTax,
});

/** How a price is quoted: its own way, else as its supplier usually quotes. */
export const quoteOf = (price: Pick<SupplierPriceRow, "quote">, details: Pick<SupplierDetails, "pricesIncludeTax" | "purchaseTaxRateUuid"> | undefined): TaxQuote =>
    price.quote ?? usualQuote(details ?? { pricesIncludeTax: false, purchaseTaxRateUuid: "" });

/** A quote's tax rate as a fraction (its own, else the account default). */
export const quoteTax = (quote: Pick<TaxQuote, "rateUuid">, rates: readonly TaxRate[]) => fraction(rateFor(quote.rateUuid, rates));

/** A price as quoted, from what's stored. */
export const quotedPrice = (priceNet: string, quote: Pick<TaxQuote, "includesTax">, tax: number) => toCents(fromNet(toNumber(priceNet), quote.includesTax, tax));

/** A typed price (as quoted), as stored: before tax. */
export const storedPrice = (typed: string, quote: Pick<TaxQuote, "includesTax">, tax: number) => toStored(toNet(toNumber(typed), quote.includesTax, tax));

/** What one really costs the business, before any sales tax: with purchase tax only when it can't be claimed back. */
export const realCost = (priceNet: string, tax: number, settings: Pick<PriceListSettings, "purchaseTaxClaimed">) =>
    settings.purchaseTaxClaimed ? toNumber(priceNet) : toNumber(priceNet) * (1 + tax);

/** The item's ServiceM8 cost for a real cost: in the item's basis, at its sales tax rate. */
export const itemCost = (real: number, item: { price_includes_taxes: boolean }, itemTax: number) =>
    toCents(fromNet(real, item.price_includes_taxes, itemTax));

/** An item's ServiceM8 cost for a cost typed as quoted (at the quote's rate, `tax`). */
export const costFromQuoted = (
    typed: string,
    quote: Pick<TaxQuote, "includesTax">,
    tax: number,
    item: { price_includes_taxes: boolean },
    itemTax: number,
    settings: Pick<PriceListSettings, "purchaseTaxClaimed">,
) => itemCost(realCost(storedPrice(typed, quote, tax), tax, settings), item, itemTax);

/** An item's ServiceM8 cost as quoted (at the quote's rate, `tax`): the other way from costFromQuoted. */
export function quotedCost(
    item: { cost: string; price_includes_taxes: boolean },
    itemTax: number,
    quote: Pick<TaxQuote, "includesTax">,
    tax: number,
    settings: Pick<PriceListSettings, "purchaseTaxClaimed">,
) {
    const real = toNet(toNumber(item.cost), item.price_includes_taxes, itemTax);
    const net = settings.purchaseTaxClaimed ? real : real / (1 + tax);
    return toCents(fromNet(net, quote.includesTax, tax));
}

/**
 * An item's typed cost as its box shows it: exactly as last typed (`typed`, in `quote`) while
 * ServiceM8's cost is still what that gives (ServiceM8 keeps it to the cent in the item's basis,
 * which can't always give a typed figure back), else worked out from ServiceM8's (e.g. changed there).
 */
export function shownTypedCost(
    item: { cost: string; price_includes_taxes: boolean },
    itemTax: number,
    quote: Pick<TaxQuote, "includesTax">,
    tax: number,
    settings: Pick<PriceListSettings, "purchaseTaxClaimed">,
    typed: string | null,
) {
    if (typed !== null && costFromQuoted(typed, quote, tax, item, itemTax, settings) === toCents(toNumber(item.cost))) return toCents(toNumber(typed));
    return quotedCost(item, itemTax, quote, tax, settings);
}

/** The prices with what each really costs (with `taxOf` each price's tax rate as a fraction), cheapest first. */
export function byRealCost(prices: readonly SupplierPriceRow[], taxOf: (price: SupplierPriceRow) => number, settings: Pick<PriceListSettings, "purchaseTaxClaimed">) {
    // Prices with no amount yet come last (as costing infinitely much: they never set a cost).
    return prices
        .map((p) => ({ price: p, real: hasAmount(p) ? realCost(p.priceNet, taxOf(p), settings) : Number.POSITIVE_INFINITY }))
        .sort((a, b) => (a.real === b.real ? 0 : a.real - b.real));
}

/**
 * Whether a supplier price has an amount. One without ("" stored) records where to get an item
 * before its price is known: it's listed, but never compared or followed for the cost.
 */
export const hasAmount = (p: Pick<SupplierPriceRow, "priceNet">) => p.priceNet.trim() !== "";

export type PriceAge = "current" | "recheck" | "outdated";

/** How long ago a price was checked, against the recheck and out-of-date ages (Settings). */
export function ageOf(checkedAt: number, settings: Pick<PriceListSettings, "recheckDays" | "outdatedDays">, now = Date.now()): PriceAge {
    const days = (now - checkedAt) / 86_400_000;
    if (days >= settings.outdatedDays) return "outdated";
    return days >= settings.recheckDays ? "recheck" : "current";
}

/** A new price this far (a fraction) up or down from the last one is often a typo (15 for 1.50). */
const BIG_JUMP = 0.5;

/**
 * A price change big enough to point out once it's saved, with an Undo: "10× the last price", "60%
 * less than the last price". Null when it's not that big (or there was no price before).
 */
export function bigJump(from: number, to: number): string | null {
    if (!(from > 0) || Math.abs(to - from) / from <= BIG_JUMP) return null;
    if (to >= from * 2) return `${Number((to / from).toFixed(1))}× the last price`;
    return `${Math.round((Math.abs(to - from) / from) * 100)}% ${to > from ? "more" : "less"} than the last price`;
}


/** A price with what it really costs (from byRealCost). */
export interface RankedPrice {
    price: SupplierPriceRow;
    real: number;
}

/**
 * Where an item's followed cost comes from: "chosen" (the price picked for it, bulk or not),
 * "cheapest" (the cheapest without a minimum), "bulkOnly" (every price has a minimum, so the
 * cheapest of those), or "chosenMissing" (the picked price is gone, so the cheapest).
 */
export type CostReason = "chosen" | "cheapest" | "bulkOnly" | "chosenMissing";

export interface CostPick {
    pick: RankedPrice;
    reason: CostReason;
    /**
     * A cheaper price worth knowing about: the cheapest bulk price passed over (when following the
     * cheapest), or the cheapest price when a dearer one was chosen.
     */
    cheaper: RankedPrice | null;
}

const isBulk = (r: RankedPrice) => r.price.minQty > 1;

/** The cheapest price, never a bulk price while there's one without a minimum. */
function cheapestOf(ranked: readonly RankedPrice[], missing: boolean): CostPick | null {
    const pick = ranked.find((r) => !isBulk(r)) ?? ranked[0];
    if (!pick) return null;
    let reason: CostReason = isBulk(pick) ? "bulkOnly" : "cheapest";
    if (missing) reason = "chosenMissing";
    // The first price, if it isn't the pick, is a cheaper bulk price that was passed over.
    const passed = ranked[0] === pick ? null : (ranked[0] ?? null);
    return { pick, reason, cheaper: passed };
}

/**
 * The price an item's cost follows, from its prices cheapest first: the one picked for it (any,
 * bulk included), else the cheapest without a minimum. How long since a price was checked doesn't
 * count: that would change costs with no one editing anything, so an old price is flagged instead.
 */
export function costPrice(all: readonly RankedPrice[], chosenPriceId: string | null): CostPick | null {
    // Only prices with an amount can set a cost (one without is just where to get it).
    const ranked = all.filter((r) => hasAmount(r.price));
    const chosen = chosenPriceId ? ranked.find((r) => r.price.id === chosenPriceId) : undefined;
    if (!chosen) return cheapestOf(ranked, chosenPriceId !== null);
    const cheapest = cheapestOf(ranked, false)?.pick;
    return { pick: chosen, reason: "chosen", cheaper: cheapest && cheapest.real < chosen.real ? cheapest : null };
}
